import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Heart, Loader2, Check } from "lucide-react";
import { fetchSatisfaction, saveSatisfaction, formatApiError } from "@/lib/api";

export const LEVELS = [
  { score: 1, label: "Very unhappy", color: "#ef4444" },
  { score: 2, label: "Unhappy", color: "#f97316" },
  { score: 3, label: "A bit let down", color: "#f59e0b" },
  { score: 4, label: "Okay", color: "#eab308" },
  { score: 5, label: "Good", color: "#84cc16" },
  { score: 6, label: "Really good", color: "#22c55e" },
  { score: 7, label: "Delighted", color: "#10b981" },
];

export const SatisfactionMeter = ({ readOnly = false, value = 0, compact = false }) => {
  const [score, setScore] = useState(value);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState("");
  const [showComment, setShowComment] = useState(false);

  useEffect(() => {
    if (readOnly) {
      setScore(value);
      return;
    }
    fetchSatisfaction()
      .then((d) => {
        setScore(d.score || 0);
        setComment(d.comment || "");
        setSavedAt(d.updated_at || "");
      })
      .catch(() => {});
  }, [readOnly, value]);

  const shown = hover || score;
  const active = LEVELS.find((l) => l.score === shown);

  const commit = async (next, note = comment) => {
    setScore(next);
    setSaving(true);
    try {
      const saved = await saveSatisfaction({ score: next, comment: note });
      setSavedAt(saved.updated_at);
      setShowComment(true);
      if (note === comment) toast.success(`Saved — thank you for the ${next}/7.`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const bar = (
    <div
      className="flex items-stretch gap-1.5"
      onMouseLeave={() => setHover(0)}
      data-testid="satisfaction-bar"
    >
      {LEVELS.map((l) => {
        const filled = shown >= l.score;
        return (
          <button
            key={l.score}
            type="button"
            disabled={readOnly || saving}
            data-testid={`satisfaction-step-${l.score}`}
            aria-label={`${l.score} out of 7 — ${l.label}`}
            onMouseEnter={() => !readOnly && setHover(l.score)}
            onClick={() => !readOnly && commit(l.score)}
            className={`group relative flex-1 ${readOnly ? "cursor-default" : "cursor-pointer"}`}
          >
            <motion.span
              animate={{
                backgroundColor: filled ? (active?.color || l.color) : "rgba(255,255,255,0.06)",
                height: compact ? 10 : filled ? 34 : 22,
                opacity: filled ? 1 : 0.7,
              }}
              transition={{ type: "spring", stiffness: 320, damping: 22 }}
              className="block w-full rounded-full"
              style={{ boxShadow: filled ? `0 0 14px ${active?.color || l.color}66` : "none" }}
            />
            {!compact && (
              <span className="mt-2 block font-mono text-[10px] text-steel-muted transition-colors group-hover:text-steel-text">
                {l.score}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );

  if (readOnly) {
    return (
      <div data-testid="satisfaction-readonly">
        {bar}
        <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-steel-muted">
          {score ? `${score}/7 · ${LEVELS.find((l) => l.score === score)?.label}` : "Not rated yet"}
        </p>
      </div>
    );
  }

  return (
    <div className="glass border border-steel-border p-8" data-testid="satisfaction-card">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center border border-steel-border">
            <Heart className="h-4 w-4 text-steel-brass" />
          </div>
          <div>
            <h3 className="font-display text-lg font-medium text-steel-text">How are we doing?</h3>
            <p className="text-xs text-steel-muted">Tap a bar — 1 is rough, 7 is delighted. Change it any time.</p>
          </div>
        </div>
        {saving ? (
          <Loader2 className="h-4 w-4 animate-spin text-steel-muted" />
        ) : savedAt ? (
          <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-400">
            <Check className="h-3 w-3" /> Saved
          </span>
        ) : null}
      </div>

      <div className="mt-7">{bar}</div>

      <motion.p
        key={shown}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="mt-4 font-display text-xl font-medium"
        style={{ color: active?.color || "#8b8b8b" }}
        data-testid="satisfaction-label"
      >
        {active ? `${shown}/7 — ${active.label}` : "Not rated yet"}
      </motion.p>

      {(showComment || comment) && (
        <div className="mt-5">
          <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-steel-muted">
            Anything you'd like us to know? (optional)
          </label>
          <textarea
            data-testid="satisfaction-comment"
            rows={2}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            onBlur={() => score && commit(score, comment)}
            placeholder="What would make this a 7 for you?"
            className="w-full resize-none border border-steel-border bg-steel-elevated px-4 py-3 text-sm text-steel-text placeholder:text-steel-muted focus:border-steel-accent focus:outline-none"
          />
        </div>
      )}
    </div>
  );
};

export default SatisfactionMeter;
